import { useMemo, useRef } from 'react';

import { Textarea, TextareaProps } from '@mantine/core';

import classes from './LinedTextarea.module.css';

/**
 * A Textarea for "one line = one item" input (subject lines) that makes
 * the LOGICAL lines visible: each line gets an alternating background
 * stripe, and wrapped continuations share their line's stripe — so a
 * colour change always means a real newline, no matter how far a
 * paragraph-style line wraps.
 *
 * Implementation: a mirror backdrop renders the same text (transparent
 * ink) behind a transparent-background textarea. Both elements pin the
 * same font, padding and wrapping metrics via the CSS module, and the
 * backdrop scroll-syncs for the overflowing (maxRows) case. The mirror
 * mounts inside Mantine's `inputContainer`, so label/description/error
 * render outside it untouched.
 *
 * Controlled usage only (the stripes derive from `value`). Disabled
 * inputs fall back to a plain Textarea — stripes on an uneditable field
 * are noise, and Mantine's disabled background would fight the overlay.
 */
export function LinedTextarea(props: Readonly<TextareaProps>) {
  const backdropRef = useRef<HTMLDivElement>(null);

  const value = typeof props.value === 'string' ? props.value : '';
  const lines = useMemo(() => value.split('\n'), [value]);

  if (props.disabled) {
    return <Textarea {...props} />;
  }

  return (
    <Textarea
      {...props}
      classNames={{ ...props.classNames, input: classes.input }}
      inputContainer={(children) => (
        <div className={classes.wrapper}>
          <div ref={backdropRef} className={classes.backdrop} aria-hidden="true">
            {lines.map((line, i) => (
              <div
                // Index keys are correct here: the list is a pure
                // projection of `value` re-rendered wholesale.

                key={i}
                className={i % 2 === 1 ? classes.lineOdd : classes.lineEven}
                data-testid={`lined-textarea-stripe-${i}`}
              >
                {line === '' ? ' ' : line}
              </div>
            ))}
          </div>
          {children}
        </div>
      )}
      onScroll={(e) => {
        if (backdropRef.current) {
          backdropRef.current.scrollTop = e.currentTarget.scrollTop;
        }
        props.onScroll?.(e);
      }}
    />
  );
}
